import { Platform } from 'react-native';
import { translate } from 'secullum-i18n';
import { Coordenadas, PerimetroAutorizado } from './types';
import { isFirefox } from './utils';

export interface GeolocationCoordinates {
  latitude?: number | null;
  longitude?: number | null;
  accuracy?: number | null;
  address?: string;
}

interface WatchGeolocationOptions {
  onChange: (coords: GeolocationCoordinates) => void;
  onError: (error: string) => void;
  detectAddress?: boolean;
}

export const BROWSER_NO_SUPPORT_GEOLOCATION =
  'Navegador não possuí suporte a localização';

export const getGeolocationError = (error: GeolocationPositionError) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return translate('Sem acesso à localização');
    case error.POSITION_UNAVAILABLE:
      return translate('Informações sobre a localização indisponíveis');
    case error.TIMEOUT:
      return translate('Tempo excedido');
    default:
      return translate('Erro desconhecido');
  }
};

export async function reverseGeocodeAsync(
  lat: number,
  lng: number
): Promise<string> {
  try {
    const response = await fetch(
      'https://geolocalizacao.secullum.com.br/Reverse?latitude=' +
        lat +
        '&longitude=' +
        lng
    );

    const data = await response.json();

    if (!data) {
      return '';
    }

    return data.endereco;
  } catch {
    // Caso ocorra algum erro na requisição, deve ficar sem endereço.
    return '';
  }
}

export const validarBatidaDentroPerimetro = (
  lat: number,
  lng: number,
  perimetrosAutorizados: PerimetroAutorizado[]
) => {
  return perimetrosAutorizados.some(perimetro => {
    if (perimetro.circulo && perimetro.radius) {
      const circulo = perimetro.coordenadas[0];

      if (estaDentroCirculo(lat, lng, circulo, perimetro.radius)) {
        return true;
      }
    } else if (temInterseccao(lat, lng, perimetro.coordenadas)) {
      return true;
    }

    return false;
  });
};

const temInterseccao = (
  lat: number,
  lng: number,
  coordenadas: Coordenadas[]
) => {
  let interseccao = false;

  for (let i = 0, j = coordenadas.length - 1; i < coordenadas.length; j = i++) {
    const xi = coordenadas[i].lat;
    const yi = coordenadas[i].lng;
    const xj = coordenadas[j].lat;
    const yj = coordenadas[j].lng;
    const iMaiorQueLng = yi > lng;
    const jMaiorQueLng = yj > lng;

    if (
      iMaiorQueLng !== jMaiorQueLng &&
      lat < ((xj - xi) * (lng - yi)) / (yj - yi) + xi
    ) {
      interseccao = !interseccao;
    }
  }

  return interseccao;
};

const estaDentroCirculo = (
  lat: number,
  lng: number,
  coordenadas: Coordenadas,
  raio: number
) => {
  return (
    distanciaEntrePontos(coordenadas.lat, coordenadas.lng, lat, lng) <= raio
  );
};

const distanciaEntrePontos = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) => {
  if (lat1 === lat2 && lng1 === lng2) {
    return 0;
  }

  const thetaRad = grauParaRadiano(lng1 - lng2);
  const lat1Rad = grauParaRadiano(lat1);
  const lat2Rad = grauParaRadiano(lat2);

  let distance =
    Math.sin(lat1Rad) * Math.sin(lat2Rad) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.cos(thetaRad);

  distance = radianoParaGrau(Math.acos(distance)) * 60 * 1.1515;

  // Converte para metros
  return distance * 1.609344 * 1000;
};

const grauParaRadiano = (deg: number) => {
  return (deg * Math.PI) / 180.0;
};

const radianoParaGrau = (rad: number) => {
  return (rad / Math.PI) * 180.0;
};

export const watchGeolocationAsync = async (
  options: WatchGeolocationOptions
) => {
  let lastCoords: GeolocationCoordinates = {};
  let lastAddress = '';

  const onChange = async (coords: GeolocationCoordinates) => {
    const changedCoords =
      lastCoords.latitude?.toFixed(4) !== coords.latitude?.toFixed(4) ||
      lastCoords.longitude?.toFixed(4) !== coords.longitude?.toFixed(4);

    if (options.detectAddress && (!lastAddress || changedCoords)) {
      const address = await reverseGeocodeAsync(
        coords.latitude ?? 0,
        coords.longitude ?? 0
      );

      lastAddress = address;
    }

    options.onChange({
      longitude: coords.longitude,
      latitude: coords.latitude,
      accuracy: coords.accuracy,
      address: lastAddress
    });
    lastCoords = coords;
  };

  const newOptions = { ...options, onChange };

  if (Platform.OS === 'web') {
    return watchGeolocationWeb(newOptions);
  }

  return await watchGeolocationMobileAsync(newOptions);
};

const watchGeolocationWeb = (options: WatchGeolocationOptions) => {
  if (!navigator.geolocation) {
    options.onError(translate(BROWSER_NO_SUPPORT_GEOLOCATION));
    return;
  }

  const innerOptions = {
    watchId: setupWebWatchPosition(options),
    isFirefox: isFirefox()
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      navigator.geolocation.clearWatch(innerOptions.watchId);
      innerOptions.watchId = 0;
    } else {
      innerOptions.watchId = setupWebWatchPosition(options);
    }
  };

  // O Firefox não permite realizar watchPosition sem foco na tela, ocasionando timeout.
  // Com base nisso adicionamos um EventListener para parar o watchPosition quando a tela
  // perde foco e retomá-lo quando o foco é recuperado.
  if (innerOptions.isFirefox) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  return () => {
    navigator.geolocation.clearWatch(innerOptions.watchId);

    if (innerOptions.isFirefox) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  };
};

const watchGeolocationMobileAsync = async (
  options: WatchGeolocationOptions
) => {
  const location = await import('expo-location');

  await location.requestForegroundPermissionsAsync();

  try {
    const geolocationOptions = {
      accuracy: location.Accuracy.Highest,
      timeInterval: 10000,
      distanceInterval: 0
    };

    const response = await location.watchPositionAsync(
      geolocationOptions,
      ({ coords }) => {
        options.onChange(coords);
      }
    );

    return response.remove;
  } catch {
    options.onError(translate('Sem acesso à localização'));
    return;
  }
};

const setupWebWatchPosition = (options: WatchGeolocationOptions) => {
  return navigator.geolocation.watchPosition(
    ({ coords }) => {
      options.onChange(coords);
    },
    error => {
      options.onError(getGeolocationError(error));
    },
    { enableHighAccuracy: true, timeout: 30000, maximumAge: 1000 }
  );
};
